import React from "react";
import moment from "moment";
import { Controller } from "react-hook-form";
import DatePicker from "react-datepicker"; // Assuming you're using react-datepicker
import "react-datepicker/dist/react-datepicker.css"; // Import default styles
import { FormMessage } from "../FormMessage";
import { cn } from "helper/Utils";
import CustomYearAndMonthSelect from "./CustomYearMonthSelect";

// Utility function to generate a range of numbers
const range = (start, end, step = 1) => Array.from({ length: Math.ceil((end - start) / step) + 1 }, (_, i) => start + i * step);

const InputDatePicker = ({
  className,
  label,
  name,
  date,
  validations,
  disabled,
  readOnly,
  placeholder,
  showDisabledMonthNavigation,
  defaultValue,
  minDate,
  maxDate,
  startDate,
  endDate,
  selectsStart,
  selectsEnd,
  showYearDropdown = true,
  showMonthDropdown = true,
  isClearable = false,
  yearsRange = range(1990, moment().year(), 1), // Replaced `getYear(new Date())`
  postfix,
  postfixAction = () => {},
  control,
  currentError,
  ...props
}) => {
  const months = moment.months(); // ["January", "February", ..., "December"]

  return (
    <>
      <style jsx="true">
        {`
          .react-datepicker-wrapper {
            width: 100%;
          }
          .react-datepicker * {
            color: rgba(255, 255, 255, 1);
          }
          .react-datepicker {
            border-color: rgba(255, 255, 255, 0.25);
            background: none;
          }
          .react-datepicker .react-datepicker__header {
            border-color: rgba(255, 255, 255, 0.25);
            background: rgba(255, 255, 255, 0.1);
            color: white;
          }
          .react-datepicker .react-datepicker__day {
            border-radius: 4px;
          }
          .react-datepicker .react-datepicker__day--selected,
          .react-datepicker .react-datepicker__day--keyboard-selected,
          .react-datepicker .react-datepicker__day--keyboard-selected.react-datepicker__day--today {
            background: #e3910a !important;
          }
          .react-datepicker .react-datepicker__day--disabled {
            color: rgba(255, 255, 255, 0.6);
          }
          .react-datepicker .react-datepicker__day:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}
      </style>
      <div className={cn("input-group group relative w-full", className)}>
        {/* Input Date Picker */}
        {control ? (
          <Controller
            name={name}
            control={control}
            rules={validations}
            render={({ field }) => (
              <DatePicker
                {...field}
                renderCustomHeader={({
                  date,
                  changeYear,
                  changeMonth,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div
                    className="flex gap-1"
                    onMouseDown={(e) => e.stopPropagation()} // Prevent parent click events
                  >
                    <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                      <i className="bc-i-small-arrow-left text-md !text-white/60 mx-1" />
                    </button>
                    <CustomYearAndMonthSelect
                      className="min-w-28"
                      value={months[moment(date).month()]} // Replaced `getMonth(date)`
                      defaultValue={months[0]}
                      options={months}
                      onChange={(selected) => changeMonth(months.indexOf(selected))}
                    />
                    <CustomYearAndMonthSelect
                      value={moment(date).year()} // Replaced `getYear(date)`
                      options={yearsRange}
                      onChange={(selected) => changeYear(selected)}
                    />
                    <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                      <i className="bc-i-small-arrow-right text-md !text-white/60 mx-1" />
                    </button>
                  </div>
                )}
                calendarClassName="custom-calendar !bg-body"
                autoComplete="off"
                className={cn(
                  "leading-none w-full text-sm rounded bg-white/15 text-white pb-[8px] pt-[26px] pr-8 pl-4",
                  "focus:outline-none group-[focus]:-outline-none",
                  "disabled:bg-white/5 ",
                  className
                )}
                selected={field.value ? moment(field.value).toDate() : date || defaultValue}
                placeholderText={placeholder}
                minDate={minDate}
                maxDate={maxDate}
                startDate={startDate}
                endDate={endDate}
                selectsStart={selectsStart}
                selectsEnd={selectsEnd}
                showYearDropdown={showYearDropdown}
                showMonthDropdown={showMonthDropdown}
                isClearable={isClearable}
                disabled={disabled}
                readOnly={readOnly}
                {...props}
              />
            )}
          />
        ) : (
          <DatePicker
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div
                className="flex gap-1"
                onMouseDown={(e) => e.stopPropagation()} // Prevent parent click events
              >
                <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                  <i className="bc-i-small-arrow-left text-md !text-white/60 mx-1" />
                </button>
                <CustomYearAndMonthSelect
                  className="min-w-28"
                  value={months[moment(date).month()]}
                  defaultValue={months[0]}
                  options={months}
                  onChange={(selected) => changeMonth(months.indexOf(selected))}
                />
                <CustomYearAndMonthSelect value={moment(date).year()} options={yearsRange} onChange={(selected) => changeYear(selected)} />
                <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                  <i className="bc-i-small-arrow-right text-md !text-white/60 mx-1" />
                </button>
              </div>
            )}
            calendarClassName="custom-calendar !bg-body"
            autoComplete="off"
            className={cn(
              "leading-none w-full text-sm rounded bg-white/15 text-white pb-[8px] pt-[26px] pr-8 pl-4",
              "focus:outline-none group-[focus]:-outline-none",
              "disabled:bg-white/5 ",
              className
            )}
            selected={date ? moment(date).toDate() : defaultValue}
            placeholderText={placeholder}
            minDate={minDate}
            maxDate={maxDate}
            startDate={startDate}
            endDate={endDate}
            selectsStart={selectsStart}
            selectsEnd={selectsEnd}
            showYearDropdown={showYearDropdown}
            showMonthDropdown={showMonthDropdown}
            isClearable={isClearable}
            disabled={disabled}
            readOnly={readOnly}
            {...props}
          />
        )}
        <span onClick={postfixAction} className="absolute inset-y-0 flex items-center h-full right-3 text-[20px] text-[rgb(255,255,255,.8)]">
          <i className="bc-i-datepicker -mt-0.5" />
        </span>
        {label && (
          <label htmlFor={name} className="absolute top-[10px] left-4 text-[12px] text-white/50">
            {label}
          </label>
        )}
        {currentError && <FormMessage message={currentError} />}
      </div>
    </>
  );
};

export default InputDatePicker;
